/**
 * The four-section notice drawer (#1911).
 *
 * Sections 1–2 (copy, audience) are live from the moment the drawer opens;
 * sections 3 and 4 (the two send gates) are inert until the draft has been
 * saved, because every endpoint behind them is addressed by notice id. That is
 * stated on screen rather than hidden — a section that looked editable and
 * silently did nothing would be worse than one that says what it is waiting
 * for.
 *
 * Editing a saved draft CLEARS BOTH GATES server-side, along with the frozen
 * recipient rows: a list computed for a different audience is not evidence
 * about this one, and copy that was rehearsed is not the copy that would now
 * go out. The drawer says so before the operator saves, since from their side
 * the only visible effect is two green lamps going grey.
 */
import { useEffect, useState } from 'react';
import type {
  EmailNoticeDetail,
  EmailNoticeRecipientPreviewResponse,
  ModelRecord,
} from '@nadoutong/contracts';
import { useModelsList } from '../../api/useModels.ts';
import { useEmailTemplates } from '../../api/useEmailTemplates.ts';
import {
  useCancelEmailNotice,
  useCreateEmailNotice,
  useFinalizeEmailNoticeRecipients,
  usePreviewEmailNoticeAudience,
  useSendEmailNotice,
  useTestSendEmailNotice,
  useUpdateEmailNotice,
} from '../../api/useEmailNotices.ts';
import { coerceErrorMessage, useToast } from '../Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Drawer, Panel } from '../../ui/index.ts';
import { NoticeDraftForm } from './NoticeDraftForm.tsx';
import {
  EMPTY_DRAFT,
  isDraftSaveable,
  sameAudience,
  toAudience,
  toNoticeRequest,
  type NoticeDraft,
} from './noticeDraft.ts';
import { NoticeDeliveryTable } from './NoticeDeliveryTable.tsx';
import { NoticeRecipientsSection } from './NoticeRecipientsSection.tsx';
import { NoticeCancelDialog } from './NoticeLifecycleDialogs.tsx';
import { NoticeSendSection } from './NoticeSendSection.tsx';
import { runBatchedNoticeSend, type NoticeSendProgress } from './noticeBatchSend.ts';
import { downloadRecipientsCsv } from './recipientExport.ts';

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** `null` while composing a brand-new notice. */
  readonly detail: EmailNoticeDetail | null;
  readonly detailLoading: boolean;
  /**
   * Hand the freshly created notice's id back so the page can point this drawer
   * at it. Without it the drawer stays on `detail === null` after a successful
   * create: sections 3 and 4 keep saying "save the draft first" about a draft
   * that IS saved, the footer button keeps saying "create", and pressing it
   * again posts a SECOND draft with no warning. The create response is already
   * seeded into the detail cache, so this costs no request.
   */
  readonly onCreated: (id: string) => void;
}

function draftFrom(detail: EmailNoticeDetail | null): NoticeDraft {
  if (detail === null) return EMPTY_DRAFT;
  return {
    taskTitle: detail.notice.task_title,
    subject: detail.notice.subject,
    body: detail.notice.body,
    audience: {
      type: detail.notice.audience.type,
      models: detail.notice.audience.models,
      windowDays: String(detail.notice.audience.window_days),
    },
    remindOn: detail.notice.remind_on ?? '',
  };
}

export function NoticeDrawer({
  open,
  onOpenChange,
  detail,
  detailLoading,
  onCreated,
}: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const modelsQuery = useModelsList();
  const models: readonly ModelRecord[] = modelsQuery.data ?? [];
  const templatesQuery = useEmailTemplates();

  const [draft, setDraft] = useState<NoticeDraft>(EMPTY_DRAFT);
  const [preview, setPreview] = useState<EmailNoticeRecipientPreviewResponse | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [sendProgress, setSendProgress] = useState<NoticeSendProgress | null>(null);

  const noticeId = detail?.notice.id ?? null;
  useEffect(() => {
    setDraft(draftFrom(detail));
    // A preview belongs to one notice and one instant. Carrying it across a
    // drawer switch would show one notice's candidates under another's header.
    setPreview(null);
    // Proofs are NOT reset here: they arrive on the detail itself
    // (`proven_brands`), so they are already scoped to this notice and already
    // correct on a cold load.
  }, [noticeId, open]);

  const create = useCreateEmailNotice();
  const update = useUpdateEmailNotice();
  const previewMutation = usePreviewEmailNoticeAudience();
  const finalize = useFinalizeEmailNoticeRecipients();
  const testSend = useTestSendEmailNotice();
  const send = useSendEmailNotice();
  const cancel = useCancelEmailNotice();

  const notice = detail?.notice ?? null;
  const isDraft = notice === null || notice.status === 'draft';
  const saveable = isDraftSaveable(draft);

  const fail = (error: unknown): void => {
    toast.error(coerceErrorMessage(error, t('email_notices.error.generic')));
  };

  const save = (): void => {
    const body = toNoticeRequest(draft);
    if (body === null) return;
    if (noticeId === null) {
      create.mutate(body, {
        onSuccess: (created) => {
          // Take over the new id BEFORE the toast, so the two gate sections
          // become live in the same render the operator is told it worked.
          onCreated(created.notice.id);
          toast.success(t('email_notices.toast.created'));
        },
        onError: fail,
      });
      return;
    }
    update.mutate(
      { id: noticeId, body },
      {
        onSuccess: () => {
          setPreview(null);
          // An accepted edit DELETES the server-side proof rows. Nothing to
          // clear locally: the PATCH response IS the new detail and carries the
          // emptied `proven_brands`, which `seedDetail` has already written.
          toast.success(t('email_notices.toast.updated'));
        },
        onError: fail,
      },
    );
  };

  /**
   * ONE preview path for a saved draft and an unsaved one (#1920).
   *
   * It asks about the audience currently in the FORM, so the count an operator
   * gets while composing is the count for the selection in front of them. That
   * is deliberately not the same question as the freeze, which recomputes from
   * the STORED audience server-side: with unsaved audience edits on screen the
   * two can differ, which is why an edit still clears both gates and why the
   * frozen list is rendered again in full before any send.
   */
  const previewAudience = toAudience(draft.audience);
  const runPreview = (): void => {
    if (previewAudience === null) return;
    previewMutation.mutate(previewAudience, { onSuccess: setPreview, onError: fail });
  };

  const runFinalize = (excludeAccountIds: readonly string[]): void => {
    if (noticeId === null) return;
    finalize.mutate(
      { id: noticeId, excludeAccountIds },
      {
        onSuccess: (result) => {
          // Back to the frozen view: the candidate list has served its purpose
          // and what matters now is the rows that will actually be mailed.
          setPreview(null);
          toast.success(
            t('email_notices.toast.finalized', { count: String(result.recipients.length) }),
          );
        },
        onError: fail,
      },
    );
  };

  const runTestSend = (to: string): void => {
    if (noticeId === null) return;
    testSend.mutate(
      { id: noticeId, body: { to } },
      {
        // The proof set itself is folded into the cached detail by the mutation
        // hook. A successful rehearsal now always clears the gate outright —
        // there is one sender, and this call just proved it — so there is no
        // partial outcome left to report.
        onSuccess: () => toast.success(t('email_notices.toast.test_sent', { to })),
        onError: fail,
      },
    );
  };

  /**
   * Every path that mails — the main press and both retries — walks its rows
   * in batches until done (#2616). `accountIds` omitted means every row that
   * is not yet `sent`, the same set an unscoped `/send` would read.
   */
  const runSend = (accountIds?: readonly string[]): void => {
    if (noticeId === null || sendProgress !== null) return;
    const ids =
      accountIds ??
      (detail?.recipients ?? []).filter((row) => row.status !== 'sent').map((row) => row.account_id);
    void runBatchedNoticeSend({
      accountIds: ids,
      sendBatch: (batch) => send.mutateAsync({ id: noticeId, accountIds: batch }),
      onProgress: setSendProgress,
    })
      .then((result) => {
        // Reported verbatim, including `deferred`. A truncated run that
        // showed only "sent N" would read exactly like a completed one.
        toast.success(
          t('email_notices.toast.sent', {
            sent: String(result.sent),
            failed: String(result.failed),
            deferred: String(result.deferred),
          }),
        );
      })
      .catch(fail)
      .finally(() => setSendProgress(null));
  };

  const busySave = create.isPending || update.isPending;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      title={
        notice === null
          ? t('email_notices.drawer.new_title')
          : t('email_notices.drawer.title', { title: notice.task_title })
      }
      description={
        notice === null
          ? t('email_notices.drawer.new_description')
          : t('email_notices.drawer.description', { id: notice.id, status: notice.status })
      }
      closeLabel={t('common.close')}
      testId="email-notice-drawer"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
          {isDraft ? (
            <Button
              loading={busySave}
              disabled={!saveable}
              onClick={save}
              data-testid="email-notice-save"
            >
              {noticeId === null
                ? t('email_notices.drawer.create')
                : t('email_notices.drawer.save')}
            </Button>
          ) : null}
        </>
      }
    >
      {detailLoading ? (
        <p className="m-0 text-dense text-paper-low">{t('email_notices.loading')}</p>
      ) : (
        <div className="flex flex-col gap-6">
          {noticeId !== null && isDraft ? (
            <p
              className="m-0 rounded border border-warn-line bg-warn-dim/50 px-2.5 py-2 text-micro text-warn"
              role="note"
              data-testid="email-notice-edit-clears-gates"
            >
              {t('email_notices.drawer.edit_clears_gates')}
            </p>
          ) : null}

          <NoticeDraftForm
            draft={draft}
            onChange={setDraft}
            models={models}
            modelsLoading={modelsQuery.isLoading}
            templates={templatesQuery.data ?? []}
            disabled={!isDraft}
            preview={{
              ready: previewAudience !== null,
              running: previewMutation.isPending,
              result: preview,
              onPreview: runPreview,
            }}
          />

          {notice === null ? (
            <p
              className="m-0 rounded border border-line-soft bg-well px-2.5 py-2 text-dense text-paper-low"
              data-testid="email-notice-gates-locked"
            >
              {t('email_notices.drawer.gates_locked')}
            </p>
          ) : (
            <>
              <NoticeRecipientsSection
                notice={notice}
                frozen={detail?.recipients ?? []}
                candidates={preview?.candidates ?? null}
                windowFrom={preview?.window_from ?? null}
                windowDays={preview?.window_days ?? notice.audience.window_days}
                previewing={previewMutation.isPending}
                finalizing={finalize.isPending}
                audienceDirty={!sameAudience(previewAudience, notice.audience)}
                onPreview={runPreview}
                onFinalize={runFinalize}
                onExport={(rows) => downloadRecipientsCsv(rows, `${notice.id}-recipients.csv`)}
              />
              <NoticeSendSection
                notice={notice}
                recipients={detail?.recipients ?? []}
                provenBrands={detail?.proven_brands ?? []}
                testSending={testSend.isPending}
                sending={sendProgress !== null}
                progress={sendProgress}
                onTestSend={runTestSend}
                onSend={() => runSend()}
              />
              {/* EVERY frozen row, not only the ones a send has touched: second
                  carrier of "recipients are visible before sending" — see the
                  `FrozenTable` header in `NoticeRecipientsSection`. */}
              <Panel caption={t('email_notices.panel.delivery')}>
                <NoticeDeliveryTable
                  recipients={detail?.recipients ?? []}
                  retrying={sendProgress !== null}
                  onRetry={(accountId) => runSend([accountId])}
                  onRetryAllFailed={(accountIds) => runSend(accountIds)}
                />
              </Panel>
              {notice.status === 'sent' || notice.status === 'cancelled' ? null : (
                <div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setConfirmCancel(true)}
                    data-testid="email-notice-cancel"
                  >
                    {t('email_notices.cancel.action')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <NoticeCancelDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        loading={cancel.isPending}
        onConfirm={() => {
          if (noticeId !== null) {
            cancel.mutate(noticeId, {
              onSuccess: () => toast.success(t('email_notices.toast.cancelled')),
              onError: fail,
            });
          }
          setConfirmCancel(false);
        }}
      />
    </Drawer>
  );
}
